import { useState } from 'react'
import { Check, Copy, KeyRound } from 'lucide-react'
import Spinner from './Spinner'
import { Chip, ErrorNote, RowShell } from './ProjectSettingRows'
import { useGetBugReportKeyQuery, useRotateBugReportKeyMutation } from '../services/projectDataApi'
import type { BugReportKeyStatus } from '../schemas/bug-management/report-key'
import '../pages/Settings.css'

function keyDetail(status: BugReportKeyStatus | undefined, failed: boolean): string {
  if (failed) return 'Key status unavailable.'
  if (!status) return 'Loading…'
  const key = status.key
    ? `Key ${status.key.prefix}… created ${new Date(status.key.created_at).toLocaleDateString()}.`
    : 'Create a key to get a webhook URL for the help widget or other bug reporters.'
  const unauth = status.unauthenticated
  return unauth
    ? `${key} ${unauth.count} report${unauth.count === 1 ? '' : 's'} arrived without a credential, the last on ${new Date(unauth.last_at).toLocaleDateString()}. Switch those reporters to the keyed URL.`
    : key
}

// The keyed /api/bug-report-webhook URL. Only a hash is stored, so the URL is shown once, right after rotation.
export default function BugReportKeyRow({ projectId }: { projectId: string }) {
  const query = useGetBugReportKeyQuery(projectId)
  const [rotate, rotation] = useRotateBugReportKeyMutation()
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasKey = query.currentData?.key != null

  async function handleRotate() {
    if (hasKey && !confirming) {
      setConfirming(true)
      return
    }
    setError(null)
    try {
      const pending = rotate(projectId)
      const result = await pending.unwrap()
      // Keep the key out of the mutation cache; it lives only in this row's state.
      pending.reset()
      setWebhookUrl(result.webhook_url)
      setCopied(false)
    } catch {
      setError('Could not create a key. Try again.')
    } finally {
      setConfirming(false)
    }
  }

  async function handleCopy() {
    if (!webhookUrl) return
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopied(true)
    } catch {
      setError('Unable to copy automatically. Select and copy the URL above.')
    }
  }

  return (
    <RowShell
      label="Bug-report webhook URL"
      detail={keyDetail(query.currentData, query.isError)}
      testId="settings-row-bug-report-key"
      right={
        <>
          <Chip tone={hasKey ? 'ok' : 'muted'}>{hasKey ? 'Key set' : 'No key'}</Chip>
          {confirming && (
            <button type="button" className="settings-row__cancel" onClick={() => setConfirming(false)} disabled={rotation.isLoading}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="settings-row__change"
            onClick={() => void handleRotate()}
            disabled={query.isLoading || query.isError || rotation.isLoading}
            data-testid="settings-row-bug-report-key-rotate"
            data-analytics-event="project-settings-bug-report-key-rotate"
          >
            {rotation.isLoading ? <Spinner size={12} /> : <KeyRound size={13} aria-hidden />}
            {confirming ? 'Replace key: the current URL stops working' : hasKey ? 'Rotate key' : 'Create key'}
          </button>
        </>
      }
      below={
        <>
          {webhookUrl && (
            <div className="settings-token__reveal" role="status">
              <p className="settings-token__reveal-label">Copy the webhook URL now. It won&apos;t be shown again.</p>
              <div className="settings-token__reveal-row">
                <code className="settings-token__value">{webhookUrl}</code>
                <button type="button" className="settings-token__copy-btn" onClick={() => void handleCopy()} aria-label={copied ? 'Copied' : 'Copy webhook URL'}>
                  {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button type="button" className="settings-token__dismiss-btn" onClick={() => setWebhookUrl(null)}>
                I&apos;ve copied the URL
              </button>
            </div>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
        </>
      }
    />
  )
}
